import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Download, FileSpreadsheet, RefreshCw, Database,
  Users, FileText, Receipt, Clock, Briefcase
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportsExport = () => {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/summary`);
      setReports(response.data.reports);
    } catch (error) {
      console.error('Failed to load reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (endpoint, name) => {
    setExporting(name);
    try {
      const response = await axios.get(endpoint, { responseType: 'blob' });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${name.toLowerCase().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${name} downloaded successfully`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  const getIcon = (name) => {
    if (name.includes('Project')) return Briefcase;
    if (name.includes('Quote')) return FileText;
    if (name.includes('Invoice')) return Receipt;
    if (name.includes('Labour')) return Clock;
    if (name.includes('Customer')) return Users;
    return Database;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <RefreshCw className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="reports-export">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2">
          Reports & Exports
        </h1>
        <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
          Download your data in CSV format
        </p>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reports.map((report, idx) => {
          const Icon = getIcon(report.name);
          const isExporting = exporting === report.name;
          
          return (
            <Card key={idx} className="border-border hover:border-primary/30 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
                    <Icon className="text-primary" size={24} />
                  </div>
                  <Badge variant="outline" className="font-mono text-xs">
                    {report.records.toLocaleString()} records
                  </Badge>
                </div>
                
                <h3 className="font-bold text-lg mb-2">{report.name}</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Export all {report.name.toLowerCase().replace(' export', '')} data to CSV format
                </p>
                
                <Button
                  onClick={() => handleExport(report.endpoint, report.name)}
                  disabled={isExporting || report.records === 0}
                  className="w-full font-mono uppercase text-xs"
                  variant={report.records === 0 ? "secondary" : "default"}
                >
                  {isExporting ? (
                    <><RefreshCw size={14} className="mr-2 animate-spin" /> Exporting...</>
                  ) : (
                    <><Download size={14} className="mr-2" /> Download CSV</>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info Card */}
      <Card className="border-border mt-6 bg-muted/30">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <FileSpreadsheet className="text-muted-foreground mt-1" size={24} />
            <div>
              <h3 className="font-semibold mb-2">About CSV Exports</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• CSV files can be opened in Excel, Google Sheets, or any spreadsheet application</li>
                <li>• All dates are formatted as YYYY-MM-DD for easy sorting</li>
                <li>• Currency values are exported as numbers without formatting</li>
                <li>• Large exports may take a few seconds to generate</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsExport;
